import React, { type ReactNode, useContext, useMemo } from 'react';

import { Flex } from '@banhala/ui-kit';
import { ScrollContext } from '../../context/ScrollContext';
import {
  PromotionFloatingButton,
  ScrollToBottomButton,
  ScrollToTopButton,
} from 'shared/src/component';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useGoodsPromotionComponents } from '../../queries/useGoodsPromotionComponents';
import { useClickFloatingButton } from './hooks/useClickFloatingButton';
import {
  SCROLL_ANIMATION_DURATION,
  SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
} from '../../constant/scrollValue';
import { FloatingButtonsContainer } from './components/FloatingButtonsContainer';
import { isAndroid } from 'shared/src/utils';
import { useScrollThresholdValue } from '../../hooks/useScrollThresholdValue';

const FIXED_OFFSET = 16;
const BUTTON_SIZE = 40;

type FloatingButtonsProps = {
  scrollToYPosition: number;
  // stickyItemPositionY: number;
};

const ButtonContainer: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Flex
    shadowStep={'medium'}
    width={BUTTON_SIZE}
    height={BUTTON_SIZE}
    borderRadius={BUTTON_SIZE * 0.5}
  >
    {children}
  </Flex>
);

const FloatingButtonsAnimatedView: React.FC<
  FloatingButtonsProps & { children?: ReactNode }
> = props => {
  const { listRef } = useContext(ScrollContext);
  const visibilityValue = useScrollThresholdValue(
    isAndroid
      ? props.scrollToYPosition + SCROLL_FLOATING_BUTTON_VISIBLE_VALUE
      : SCROLL_FLOATING_BUTTON_VISIBLE_VALUE,
    isAndroid ? SCROLL_ANIMATION_DURATION * 0.5 : SCROLL_ANIMATION_DURATION,
  );

  // worklet 이 props 를 캡처하면(= children React 엘리먼트 포함) Bundle Mode 직렬화가
  // FiberNode 에서 실패한다. 원시값/상수만 캡처한다.
  const style = useAnimatedStyle(() => ({
    position: 'absolute',
    bottom: FIXED_OFFSET,
    right: FIXED_OFFSET,
    alignItems: 'center',
    gap: 12,
    pointerEvents: visibilityValue.value > 0.5 ? 'auto' : 'none', // ← animatedProps에서 이리로
    opacity: isAndroid
      ? visibilityValue.value > 0.5
        ? 1
        : 0
      : visibilityValue.value,
  }));

  return (
    <Animated.View style={style}>
      <ButtonContainer>
        <ScrollToTopButton
          testID={'탑버튼'}
          onPress={() => {
            listRef?.current?.scrollToOffset({
              offset: props.scrollToYPosition,
              animated: true,
            });
          }}
        />
      </ButtonContainer>
      <ButtonContainer>
        <ScrollToBottomButton
          testID={'바텀버튼'}
          onPress={() => {
            listRef?.current?.scrollToEnd({ animated: true });
          }}
        />
      </ButtonContainer>
      {props.children}
    </Animated.View>
  );
};

const FloatingButtonContent: React.FC<FloatingButtonsProps> = props => {
  const { data: promotionComponents } =
    useGoodsPromotionComponents('GOODS_DETAIL');

  const floatingButton = useMemo(() => {
    const buttons = promotionComponents.component?.floating_buttons ?? [];
    if (buttons.length === 0) return null;
    return {
      button: buttons[Math.floor(Math.random() * buttons.length)],
      promotionSno: promotionComponents.component?.promotion_sno,
    };
  }, [
    promotionComponents.component?.floating_buttons,
    promotionComponents.component?.promotion_sno,
  ]);

  return (
    <FloatingButtonsAnimatedView {...props}>
      {floatingButton && (
        <GoodsDetailPromotionFloatingButton
          button={floatingButton.button}
          promotionSno={floatingButton.promotionSno}
        />
      )}
    </FloatingButtonsAnimatedView>
  );
};

const GoodsDetailPromotionFloatingButton: React.FC<{
  button: NonNullable<
    NonNullable<
      ReturnType<typeof useGoodsPromotionComponents>['data']['component']
    >['floating_buttons'][number]
  >;
  promotionSno?: number;
}> = ({ button, promotionSno }) => {
  const onClickLog = useClickFloatingButton(button, 0);
  return (
    <PromotionFloatingButton
      button={button}
      promotionSno={promotionSno}
      onClickLog={onClickLog}
    />
  );
};

/** BottomDockedFloatingLayer 안에서 사용한다. bottom 은 도킹 영역 상단 기준. */
export const FloatingButtons: React.FC<FloatingButtonsProps> = props => {
  return (
    <FloatingButtonsContainer>
      <React.Suspense fallback={<FloatingButtonsAnimatedView {...props} />}>
        <FloatingButtonContent {...props} />
      </React.Suspense>
    </FloatingButtonsContainer>
  );
};
